import React, { useState, useEffect, useCallback } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { toast } from 'react-toastify'
import Calendar from '../../../assets/images/calendar.png'
import { makeStyles } from '../../../utils/hooks/makeStyles'
import CampDashboardGraphical from './CampDashboardGraphical'
import { useAppSelector } from '../../../app/hooks'
import { getCampDashboardStats } from '../../../services/CampManagementService'
import { formatDate } from '../../lms/utils'

// Styles definition
const useStyles = makeStyles(() => ({
  dashboardContainer: {
    padding: '20px',
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
  },
  dashboardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: 600,
    color: '#183f7c',
    margin: 0,
  },
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: '#fff',
    padding: '8px 16px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
  },
  toggleSwitch: {
    position: 'relative',
    width: '50px',
    height: '24px',
    background: '#ccc',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background 0.3s',
    '&.active': {
      background: '#183f7c',
    },
  },
  toggleSlider: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '20px',
    height: '20px',
    background: '#fff',
    borderRadius: '50%',
    transition: 'transform 0.3s',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    '&.active': {
      transform: 'translateX(26px)',
    },
  },
  dateFilters: {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  dateFilterItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  dateLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
    whiteSpace: 'nowrap',
  },
  datePickerWrapper: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  calendarIcon: {
    width: '16px',
    height: '16px',
    marginRight: '8px',
  },
  datePickerUI: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    minWidth: '140px',
    '&:focus': {
      outline: 'none',
      borderColor: '#183f7c',
    },
  },
  fetchButton: {
    padding: '8px 20px',
    backgroundColor: '#183f7c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.3s',
    '&:hover': {
      backgroundColor: '#2563a8',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
      cursor: 'not-allowed',
    },
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#666',
  },
  dashboardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px',
  },
  dashboardSection: {
    background: '#fff',
    borderRadius: '8px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#183f7c',
    margin: '0 0 20px 0',
    paddingBottom: '12px',
    borderBottom: '2px solid #e8edf2',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '20px',
  },
  sleepAnalysisGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 240px))',
    gap: '16px',
    marginBottom: '20px',
  },
  statCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    color: '#fff',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  primaryCard: {
    background: 'linear-gradient(135deg, #183f7c 0%, #2563a8 100%)',
  },
  maleCard: {
    background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
  },
  femaleCard: {
    background: 'linear-gradient(135deg, #e91e63 0%, #c2185b 100%) !important',
  },
  successCard: {
    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%) !important',
  },
  warningCard: {
    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%) !important',
  },
  infoCard: {
    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%) !important',
  },
  dangerCard: {
    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%) !important',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: 700,
    marginBottom: '8px',
  },
  statLabel: {
    fontSize: '14px',
    opacity: 0.9,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statPercentage: {
    fontSize: '12px',
    marginTop: '6px',
    opacity: 0.8,
    fontWeight: 600,
  },
  subsectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
  },
  subsectionCard: {
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #e8edf2',
  },
  subsectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#183f7c',
    margin: '0 0 12px 0',
    paddingBottom: '8px',
    borderBottom: '1px solid #e8edf2',
  },
  listItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    background: '#fff',
    borderRadius: '6px',
    transition: 'background 0.2s',
    '&:hover': {
      background: '#f0f4f8',
    },
  },
  itemName: {
    fontSize: '14px',
    color: '#333',
    fontWeight: 500,
  },
  itemValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#183f7c',
    background: '#e8edf2',
    padding: '4px 12px',
    borderRadius: '12px',
    minWidth: '40px',
    textAlign: 'center',
  },
  visitTypesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '16px',
  },
  visitTypeCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    color: '#fff',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
    },
  },
  visitTypeName: {
    fontSize: '14px',
    marginBottom: '8px',
    opacity: 0.9,
    fontWeight: 500,
  },
  visitTypeCount: {
    fontSize: '32px',
    fontWeight: 700,
  },
  labStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
  labStatCard: {
    background: '#fff',
    border: '2px solid #e8edf2',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    transition: 'all 0.2s',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    },
    '&:hover': {
      borderColor: '#183f7c',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
  },
  labStatValue: {
    fontSize: '32px',
    fontWeight: 700,
    color: '#183f7c',
    marginBottom: '8px',
  },
  labStatLabel: {
    fontSize: '13px',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 500,
  },
  ecgPftGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
    gap: '24px',
  },
  ecgPftSection: {
    background: '#f9fafb',
    borderRadius: '8px',
    padding: '20px',
    border: '1px solid #e8edf2',
  },
  ecgPftSubsectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#183f7c',
    margin: '0 0 16px 0',
    paddingBottom: '10px',
    borderBottom: '2px solid #e8edf2',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '12px',
  },
  statsRowSecondary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  statCardSmall: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '6px',
    padding: '16px',
    textAlign: 'center',
    color: '#fff',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  },
  normalCard: {
    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%) !important',
  },
  statValueSmall: {
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: '6px',
  },
  statLabelSmall: {
    fontSize: '12px',
    opacity: 0.9,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  statPercentageSmall: {
    fontSize: '11px',
    marginTop: '4px',
    opacity: 0.8,
    fontWeight: 600,
  },
  noData: {
    textAlign: 'center',
    padding: '20px',
    color: '#999',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  '@media (max-width: 768px)': {
    dashboardContainer: {
      padding: '15px',
    },
    dashboardHeader: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    headerTitle: {
      fontSize: '24px',
    },
    dateFilters: {
      width: '100%',
      flexDirection: 'column',
      gap: '12px',
    },
    dateFilterItem: {
      width: '100%',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    datePickerWrapper: {
      width: '100%',
    },
    datePickerUI: {
      width: '100%',
    },
    statsGrid: {
      gridTemplateColumns: '1fr',
    },
    subsectionGrid: {
      gridTemplateColumns: '1fr',
    },
    visitTypesGrid: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    },
    labStatsGrid: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    },
    ecgPftGrid: {
      gridTemplateColumns: '1fr',
    },
    statsRow: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    statsRowSecondary: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    statValue: {
      fontSize: '28px',
    },
  },
  '@media (max-width: 480px)': {
    dashboardContainer: {
      padding: '10px',
    },
    dashboardSection: {
      padding: '16px',
    },
    visitTypesGrid: {
      gridTemplateColumns: '1fr',
    },
    labStatsGrid: {
      gridTemplateColumns: '1fr',
    },
  },
}))

// Type definitions for API response mapping
interface AgeGroup {
  ageRange: string
  count: number
}

interface Department {
  name: string
  count: number
}

interface VisitType {
  type: string
  count: number
}

interface RegistrationData {
  total: number
  male: number
  female: number
  departments: Department[]
  ageGroups: AgeGroup[]
}

interface BasicHealthInfoData {
  total: number
  completed: number
  incomplete: number
}

interface VisitsData {
  visitTypes: VisitType[]
}

interface LabData {
  ORDERED: number
  COLLECTED: number
  SUBMITTED: number
  APPROVED: number
  COMPLETED: number
}

interface ECGData {
  total: number
  completed: number
  incomplete: number
  normal: number
  abnormal: number
}

interface PFTData {
  total: number
  completed: number
  incomplete: number
  normal: number
  abnormal: number
}

interface GradesData {
  a: number
  b: number
  c: number
  d: number
  e: number
}

interface DashboardData {
  registrations?: RegistrationData
  basicHealthInfo?: BasicHealthInfoData
  visits?: VisitsData
  lab?: LabData
  ecg?: ECGData
  pft?: PFTData
  grades?: GradesData
  result?: Record<string, string | number>
}

interface CampDashboardProps {
  camp?: any
}

const CampDashboard: React.FC<CampDashboardProps> = ({ camp }) => {
  const classes = useStyles()
  const [fromDate, setFromDate] = useState<Date | null>(new Date())
  const [toDate, setToDate] = useState<Date | null>(new Date())
  const [dashboardData, setDashboardData] = useState<DashboardData>({})
  const [loading, setLoading] = useState(false)
  const [isGraphicalView, setIsGraphicalView] = useState(false)

  // Get selected camp from Redux store
  const selectedCamp = useAppSelector(
    (state) => state?.campManagement?.selectedCamp
  )

  // Use camp from prop if available, otherwise fall back to Redux store
  const activeCamp = camp || selectedCamp

  // Fetch dashboard data from API
  const fetchDashboardData = useCallback(async () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both From Date and To Date')
      return
    }

    const campId = activeCamp?.campId || activeCamp?.id
    if (!campId) {
      toast.error('No camp selected. Please select a camp first.')
      return
    }

    setLoading(true)
    try {
      // const fromDateStr = fromDate.toISOString().split('T')[0]
      // const toDateStr = toDate.toISOString().split('T')[0]
      const fromDateStr = formatDate(fromDate)
      const toDateStr = formatDate(toDate)

      const response = await getCampDashboardStats(
        campId,
        fromDateStr,
        toDateStr
      )

      if (response.success && response.data) {
        setDashboardData(response.data)
      } else {
        toast.error(response.message || 'Failed to fetch dashboard data')
        setDashboardData({})
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      toast.error('Failed to fetch dashboard data')
      setDashboardData({})
    } finally {
      setLoading(false)
    }
  }, [fromDate, toDate, activeCamp?.campId, activeCamp?.id])

  useEffect(() => {
    const campId = activeCamp?.campId || activeCamp?.id
    if (campId) {
      fetchDashboardData()
    }
  }, [activeCamp?.campId, activeCamp?.id, fetchDashboardData])

  const handleFromDateChange = (date: Date | null) => {
    if (date && toDate && date > toDate) {
      toast.error('From Date cannot be after To Date')
      return
    }
    setFromDate(date)
  }

  const handleToDateChange = (date: Date | null) => {
    if (date && fromDate && date < fromDate) {
      toast.error('To Date cannot be before From Date')
      return
    }
    setToDate(date)
  }

  const calculatePercentage = (value: number, total: number): string => {
    if (!value || !total || total === 0) return '0'
    const percentage = (value / total) * 100
    return isNaN(percentage) ? '0' : percentage.toFixed(1)
  }

  const handleToggleView = () => {
    setIsGraphicalView(!isGraphicalView)
  }

  // If graphical view is selected, render the graphical component
  if (isGraphicalView) {
    return (
      <CampDashboardGraphical
        dashboardData={dashboardData}
        loading={loading}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={handleFromDateChange}
        onToDateChange={handleToDateChange}
        onToggleView={handleToggleView}
        isGraphicalView={isGraphicalView}
      />
    )
  }

  return (
    <div className={classes.dashboardContainer}>
      <div className={classes.dashboardHeader}>
        <h1 className={classes.headerTitle}>Dashboard</h1>
        <div className={classes.dateFilters}>
          <div className={classes.dateFilterItem}>
            <label className={classes.dateLabel}>From Date:</label>
            <div className={classes.datePickerWrapper}>
              <img
                alt='Calendar'
                src={Calendar}
                className={classes.calendarIcon}
              />
              <DatePicker
                className={classes.datePickerUI}
                placeholderText='DD-MM-YYYY'
                selected={fromDate}
                onChange={handleFromDateChange}
                dateFormat='dd-MM-yyyy'
                maxDate={toDate || new Date()}
              />
            </div>
          </div>
          <div className={classes.dateFilterItem}>
            <label className={classes.dateLabel}>To Date:</label>
            <div className={classes.datePickerWrapper}>
              <img
                alt='Calendar'
                src={Calendar}
                className={classes.calendarIcon}
              />
              <DatePicker
                className={classes.datePickerUI}
                placeholderText='DD-MM-YYYY'
                selected={toDate}
                onChange={handleToDateChange}
                dateFormat='dd-MM-yyyy'
                minDate={fromDate}
                maxDate={new Date()}
              />
            </div>
          </div>
          <button
            className={classes.fetchButton}
            onClick={fetchDashboardData}
            disabled={
              loading ||
              !fromDate ||
              !toDate ||
              !(activeCamp?.campId || activeCamp?.id)
            }
          >
            {loading ? 'Fetching...' : 'Fetch Data'}
          </button>
          <div className={classes.toggleContainer}>
            <span className={classes.toggleLabel}>Table View</span>
            <div
              className={`${classes.toggleSwitch} ${
                isGraphicalView ? 'active' : ''
              }`}
              onClick={handleToggleView}
            >
              <div
                className={`${classes.toggleSlider} ${
                  isGraphicalView ? 'active' : ''
                }`}
              />
            </div>
            <span className={classes.toggleLabel}>Graph View</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={classes.loadingState}>Loading dashboard data...</div>
      ) : (
        <div className={classes.dashboardContent}>
          {/* Section 1: Registrations */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Registrations</h2>
            <div className={classes.statsGrid}>
              <div className={`${classes.statCard} ${classes.primaryCard}`}>
                <div className={classes.statValue}>
                  {dashboardData.registrations?.total || 0}
                </div>
                <div className={classes.statLabel}>Total</div>
              </div>
              <div className={`${classes.statCard} ${classes.maleCard}`}>
                <div className={classes.statValue}>
                  {dashboardData.registrations?.male || 0}
                </div>
                <div className={classes.statLabel}>Male</div>
              </div>
              <div className={`${classes.statCard} ${classes.femaleCard}`}>
                <div className={classes.statValue}>
                  {dashboardData.registrations?.female || 0}
                </div>
                <div className={classes.statLabel}>Female</div>
              </div>
            </div>

            <div className={classes.subsectionGrid}>
              <div className={classes.subsectionCard}>
                <h3 className={classes.subsectionTitle}>Departments</h3>
                <div className={classes.listItems}>
                  {dashboardData.registrations?.departments?.map(
                    (dept, index) => (
                      <div key={index} className={classes.listItem}>
                        <span className={classes.itemName}>{dept.name}</span>
                        <span className={classes.itemValue}>{dept.count}</span>
                      </div>
                    )
                  )}
                  {(!dashboardData.registrations?.departments ||
                    dashboardData.registrations?.departments?.length === 0) && (
                    <div className={classes.noData}>
                      No department data available
                    </div>
                  )}
                </div>
              </div>

              <div className={classes.subsectionCard}>
                <h3 className={classes.subsectionTitle}>Age Groups</h3>
                <div className={classes.listItems}>
                  {dashboardData.registrations?.ageGroups?.map(
                    (group, index) => (
                      <div key={index} className={classes.listItem}>
                        <span className={classes.itemName}>
                          {group.ageRange}
                        </span>
                        <span className={classes.itemValue}>{group.count}</span>
                      </div>
                    )
                  )}
                  {(!dashboardData.registrations?.ageGroups ||
                    dashboardData.registrations?.ageGroups?.length === 0) && (
                    <div className={classes.noData}>
                      No age group data available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Basic Health Info */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Basic Health Info</h2>
            <div className={classes.statsGrid}>
              <div className={`${classes.statCard} ${classes.primaryCard}`}>
                <div className={classes.statValue}>
                  {dashboardData.basicHealthInfo?.total || 0}
                </div>
                <div className={classes.statLabel}>Total</div>
              </div>
              <div className={`${classes.statCard} ${classes.successCard}`}>
                <div className={classes.statValue}>
                  {dashboardData.basicHealthInfo?.completed || 0}
                </div>
                <div className={classes.statLabel}>Completed</div>
                <div className={classes.statPercentage}>
                  {calculatePercentage(
                    dashboardData.basicHealthInfo?.completed || 0,
                    dashboardData.basicHealthInfo?.total || 0
                  )}
                  %
                </div>
              </div>
              <div className={`${classes.statCard} ${classes.warningCard}`}>
                <div className={classes.statValue}>
                  {dashboardData.basicHealthInfo?.incomplete || 0}
                </div>
                <div className={classes.statLabel}>Incomplete</div>
                <div className={classes.statPercentage}>
                  {calculatePercentage(
                    dashboardData.basicHealthInfo?.incomplete || 0,
                    dashboardData.basicHealthInfo?.total || 0
                  )}
                  %
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Visits */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Visits</h2>
            <div className={classes.visitTypesGrid}>
              {dashboardData.visits?.visitTypes?.map((visit, index) => (
                <div key={index} className={classes.visitTypeCard}>
                  <div className={classes.visitTypeName}>{visit.type}</div>
                  <div className={classes.visitTypeCount}>{visit.count}</div>
                </div>
              ))}
              {(!dashboardData.visits?.visitTypes ||
                dashboardData.visits?.visitTypes?.length === 0) && (
                <div className={classes.noData}>No visit data available</div>
              )}
            </div>
          </section>

          {/* Section 4: Lab */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Lab</h2>
            <div className={classes.labStatsGrid}>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.lab?.ORDERED || 0}
                </div>
                <div className={classes.labStatLabel}>Ordered</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.lab?.COLLECTED || 0}
                </div>
                <div className={classes.labStatLabel}>Collected</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.lab?.SUBMITTED || 0}
                </div>
                <div className={classes.labStatLabel}>Submitted</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.lab?.APPROVED || 0}
                </div>
                <div className={classes.labStatLabel}>Approved</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.lab?.COMPLETED || 0}
                </div>
                <div className={classes.labStatLabel}>Completed</div>
              </div>
            </div>
          </section>

          {/* Section 5: ECG/PFT */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>ECG / PFT</h2>
            <div className={classes.ecgPftGrid}>
              <div className={classes.ecgPftSection}>
                <h3 className={classes.ecgPftSubsectionTitle}>ECG</h3>
                <div className={classes.statsRow}>
                  <div className={classes.statCardSmall}>
                    <div className={classes.statValueSmall}>
                      {dashboardData.ecg?.total || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Total</div>
                  </div>
                  <div
                    className={`${classes.statCardSmall} ${classes.successCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.ecg?.completed || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Completed</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.ecg?.completed || 0,
                        dashboardData.ecg?.total || 0
                      )}
                      %
                    </div>
                  </div>
                  <div
                    className={`${classes.statCardSmall} ${classes.warningCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.ecg?.incomplete || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Incomplete</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.ecg?.incomplete || 0,
                        dashboardData.ecg?.total || 0
                      )}
                      %
                    </div>
                  </div>
                </div>
                <div className={classes.statsRowSecondary}>
                  <div
                    className={`${classes.statCardSmall} ${classes.normalCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.ecg?.normal || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Normal</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.ecg?.normal || 0,
                        dashboardData.ecg?.total || 0
                      )}
                      %
                    </div>
                  </div>
                  <div
                    className={`${classes.statCardSmall} ${classes.dangerCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.ecg?.abnormal || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Abnormal</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.ecg?.abnormal || 0,
                        dashboardData.ecg?.total || 0
                      )}
                      %
                    </div>
                  </div>
                </div>
              </div>

              <div className={classes.ecgPftSection}>
                <h3 className={classes.ecgPftSubsectionTitle}>PFT</h3>
                <div className={classes.statsRow}>
                  <div className={classes.statCardSmall}>
                    <div className={classes.statValueSmall}>
                      {dashboardData.pft?.total || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Total</div>
                  </div>
                  <div
                    className={`${classes.statCardSmall} ${classes.successCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.pft?.completed || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Completed</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.pft?.completed || 0,
                        dashboardData.pft?.total || 0
                      )}
                      %
                    </div>
                  </div>
                  <div
                    className={`${classes.statCardSmall} ${classes.warningCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.pft?.incomplete || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Incomplete</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.pft?.incomplete || 0,
                        dashboardData.pft?.total || 0
                      )}
                      %
                    </div>
                  </div>
                </div>
                <div className={classes.statsRowSecondary}>
                  <div
                    className={`${classes.statCardSmall} ${classes.normalCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.pft?.normal || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Normal</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.pft?.normal || 0,
                        dashboardData.pft?.total || 0
                      )}
                      %
                    </div>
                  </div>
                  <div
                    className={`${classes.statCardSmall} ${classes.dangerCard}`}
                  >
                    <div className={classes.statValueSmall}>
                      {dashboardData.pft?.abnormal || 0}
                    </div>
                    <div className={classes.statLabelSmall}>Abnormal</div>
                    <div className={classes.statPercentageSmall}>
                      {calculatePercentage(
                        dashboardData.pft?.abnormal || 0,
                        dashboardData.pft?.total || 0
                      )}
                      %
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 6: Grades */}
          <section className={classes.dashboardSection}>
            <h2 className={classes.sectionTitle}>Grades</h2>
            <div className={classes.labStatsGrid}>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.grades?.a || 0}
                </div>
                <div className={classes.labStatLabel}>Grade A</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.grades?.b || 0}
                </div>
                <div className={classes.labStatLabel}>Grade B</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.grades?.c || 0}
                </div>
                <div className={classes.labStatLabel}>Grade C</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.grades?.d || 0}
                </div>
                <div className={classes.labStatLabel}>Grade D</div>
              </div>
              <div className={classes.labStatCard}>
                <div className={classes.labStatValue}>
                  {dashboardData.grades?.e || 0}
                </div>
                <div className={classes.labStatLabel}>Grade E</div>
              </div>
            </div>
          </section>

          {/* Section 7: Sleep Analysis */}
          {dashboardData?.result &&
            Object.keys(dashboardData.result ?? {}).length > 0 && (
              <section className={classes.dashboardSection}>
                <h2 className={classes.sectionTitle}>Sleep Analysis</h2>
                <div className={classes.sleepAnalysisGrid}>
                  {(() => {
                    const riskColorMap: Record<string, string> = {
                      'low risk': classes.successCard,
                      'intermediate risk': classes.warningCard,
                      'high risk': classes.dangerCard,
                    }
                    const dynamicColors = [
                      classes.infoCard,
                      classes.primaryCard,
                      classes.maleCard,
                    ]
                    let dynamicColorIndex = 0

                    return Object.entries(dashboardData.result ?? {}).map(
                      ([key, value]) => {
                        const normalizedKey = key.trim().toLowerCase()
                        let colorClass = riskColorMap[normalizedKey]
                        if (!colorClass) {
                          colorClass =
                            dynamicColors[
                              dynamicColorIndex % dynamicColors.length
                            ]
                          dynamicColorIndex++
                        }
                        return (
                          <div
                            key={key}
                            className={`${classes.statCard} ${colorClass}`}
                          >
                            <div className={classes.statValue}>
                              {String(value ?? 0)}
                            </div>
                            <div className={classes.statLabel}>{key ?? ''}</div>
                          </div>
                        )
                      }
                    )
                  })()}
                </div>
              </section>
            )}
        </div>
      )}
    </div>
  )
}

export default CampDashboard
